import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { translate } from '@/services/TranslationService';
import NOVOLoader from '../../shared/components/NOVOLoader';
import { changeAndPostProfile } from '../profile/actions';
import { getFallbackRedirect, getRedirectForAction } from './actionHandlers';
import { setDeeplinkData } from './actions';
import { FALLBACK_REDIRECT_TIMEOUT_MS } from './constants';

const RouteHandler = ({ location, setHeaderTitle }) => {
  const dispatch = useDispatch();

  const isAuthenticated = useSelector(
    (state) => !!state.authReducer.auth_token
  );

  const queryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);

    return {
      action: params.get('action') || '',
      code: params.get('code') || '',
      authority: params.get('authority') || '',
      country: params.get('country') || '',
    };
  }, [location.search]);

  useEffect(() => {
    const hasDeeplink =
      !!queryParams.action ||
      !!queryParams.code ||
      !!queryParams.authority ||
      !!queryParams.country;

    if (!hasDeeplink) return;

    dispatch(
      setDeeplinkData({
        action: queryParams.action || null,
        payload_code: queryParams.code || null,
        authority: queryParams.authority || null,
        country: queryParams.country || null,
        needsDeeplinkRedirection: !!queryParams.action,
      })
    );
  }, [
    dispatch,
    queryParams.action,
    queryParams.authority,
    queryParams.code,
    queryParams.country,
  ]);

  useEffect(() => {
    const title = translate('Route handler') || 'Route handler';
    setHeaderTitle?.(title);

    const hasAuthParams = !!queryParams.authority || !!queryParams.country;

    if (hasAuthParams) {
      const updateProfile = async () => {
        try {
          await dispatch(
            changeAndPostProfile({
              authority: queryParams.authority,
              country: queryParams.country,
            })
          );
        } catch (error) {
          console.error('Failed to update profile from route handler', error);
        }
      };

      updateProfile();
    }
  }, [
    dispatch,
    isAuthenticated,
    queryParams.authority,
    queryParams.country,
    setHeaderTitle,
  ]);

  const redirectComponent = useMemo(
    () => getRedirectForAction({ ...queryParams, isAuthenticated }),
    [queryParams, isAuthenticated]
  );

  useEffect(() => {
    if (!queryParams.action) return;
    if (!redirectComponent && !fallbackRedirect) return;
  }, [dispatch, queryParams.action, redirectComponent, fallbackRedirect]);

  const [fallbackRedirect, setFallbackRedirect] = useState(false);

  useEffect(() => {
    setFallbackRedirect(false);

    if (redirectComponent) return;

    const timer = setTimeout(() => {
      setFallbackRedirect(true);
    }, FALLBACK_REDIRECT_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [queryParams.action, redirectComponent]);

  if (redirectComponent) {
    return redirectComponent;
  }

  if (fallbackRedirect) {
    return getFallbackRedirect();
  }

  return (
    <div className="flex flex-1 items-center justify-center">
      <NOVOLoader />
    </div>
  );
};

export default withRouter(RouteHandler);
